import React, { useState, useEffect } from 'react'
import { expenseService, projectService, employeeService, purchaseOrderService } from '../services/api'
import { useAuth } from '../context/AuthContext'
import TableStateWrapper from '../components/TableStateWrapper'
import TablePagination from '../components/TablePagination'
import useResponsivePageSize from '../hooks/useResponsivePageSize'
import { formatCurrency } from '../utils/currency'
import { formatDate, formatDateForInput } from '../utils/dateFormat'
import useModalFeedback from '../hooks/useModalFeedback'
import useRolePermissions from '../hooks/useRolePermissions'
import TruncatedText from '../components/TruncatedText'
import CollapsibleFilters from '../components/CollapsibleFilters'
import MultiSelectFilterDropdown from '../components/MultiSelectFilterDropdown'
import { getBaseUrl } from '../utils/apiConfig'

const API_BASE_URL = getBaseUrl()

export default function Expenses() {
  const { showMessage, showConfirm, modalElement } = useModalFeedback()
  const { user } = useAuth()
  const { isViewer, canMutate } = useRolePermissions()
  const [expenses, setExpenses] = useState([])
  const [projects, setProjects] = useState([])
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(false)
  const [tableError, setTableError] = useState(null)
  const [viewingFileInModal, setViewingFileInModal] = useState(null)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editingExpense, setEditingExpense] = useState(null)
  const [zoomLevel, setZoomLevel] = useState(1)
  const [editFormData, setEditFormData] = useState({
    amount: '',
    project_id: '',
    po_id: '',
    expense_category: '',
    description: '',
    expense_date: '',
    status: 'Pending'
  })
  const [editFiles, setEditFiles] = useState([]) // Max 1 file — overwrites previous
  const [filters, setFilters] = useState({
    years: [],
    months: [],
    project_ids: [],
    statuses: [],
    employee_ids: [],
    expense_types: [],
    project_names: []
  })
  const [expensePage, setExpensePage] = useState(1)
  const { containerRef: expenseTableRef, rowsPerPage: expenseRowsPerPage } = useResponsivePageSize({ reservedHeight: 520, footerHeight: 24, rowHeight: 52, minRows: 5, maxRows: 14 })

  // Fetch data on mount
  useEffect(() => {
    fetchExpenses()
    fetchProjects()
    fetchEmployees()
    fetchPurchaseOrders()
  }, [])

  const fetchPurchaseOrders = async () => {
    try {
      const response = await purchaseOrderService.getAll()
      setPurchaseOrders(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch purchase orders:', error)
    }
  }

  const fetchExpenses = async () => {
    try {
      setLoading(true)
      setTableError(null)
      const response = await expenseService.getAll()
      const expensesData = response.data.data || []

      // Debug logging to verify expense_category is present

      setExpenses(expensesData)
    } catch (error) {
      console.error('Failed to fetch expenses:', error)
      setTableError(error.response?.data?.message || 'Failed to fetch expenses')
    } finally {
      setLoading(false)
    }
  }

  const fetchProjects = async () => {
    try {
      const response = await projectService.getAll()
      setProjects(response.data.data || [])
    } catch (error) {
      console.error('Failed to fetch projects:', error)
    }
  }

  const fetchEmployees = async () => {
    try {
      const response = await employeeService.getAll()
      const allEmployees = response.data.data || []
      setEmployees(allEmployees)
      
      // If user is logged in, find and display their employee name
      if (user && user.employee_id) {
        const currentEmployee = allEmployees.find(emp => emp.employee_id === user.employee_id)
        if (currentEmployee) {
          setCurrentEmployeeName(currentEmployee.name)
        }
      }
    } catch (error) {
      console.error('Failed to fetch employees:', error)
    }
  }

  const parseAttachmentList = (attachmentsValue) => {
    if (!attachmentsValue) return []

    if (Array.isArray(attachmentsValue)) {
      return attachmentsValue
        .map((item) => (typeof item === 'string' ? item : (item?.path || item)))
        .filter(Boolean)
    }

    if (typeof attachmentsValue === 'string') {
      const trimmed = attachmentsValue.trim()
      if (!trimmed) return []

      try {
        const parsed = JSON.parse(trimmed)
        return parseAttachmentList(parsed)
      } catch (_error) {
        return trimmed.split(',').map((item) => item.trim()).filter(Boolean)
      }
    }

    return []
  }

  const resolveExpenseCategory = (expenseType) => {
    const normalized = String(expenseType || '').trim().toLowerCase()
    if (normalized === 'advanced') return 'advanced'
    if (normalized === 'direct') return 'direct'
    return 'receipt'
  }

  const resolveAttachmentPathByCategory = (rawPath, expenseType) => {
    const normalizedRaw = String(rawPath || '').trim()
    if (!normalizedRaw) return ''

    if (normalizedRaw.startsWith('http://') || normalizedRaw.startsWith('https://')) {
      return normalizedRaw
    }

    const slashPath = normalizedRaw.replace(/\\/g, '/')
    if (slashPath.startsWith('/uploads/')) {
      return slashPath
    }

    if (slashPath.startsWith('uploads/')) {
      return `/${slashPath}`
    }

    const expense_category = resolveExpenseCategory(expenseType)
    const fileName = slashPath.split('/').pop()
    if (!fileName) return ''

    return `/uploads/expenses/${expense_category}/${fileName}`
  }

  const resolveAttachmentUrlByCategory = (rawPath, expenseType) => {
    const resolvedPath = resolveAttachmentPathByCategory(rawPath, expenseType)
    if (!resolvedPath) return ''
    if (resolvedPath.startsWith('http://') || resolvedPath.startsWith('https://')) {
      return resolvedPath
    }
    return `${API_BASE_URL}${resolvedPath}`
  }

  const openFirstFile = (expense) => {
    if (!expense.attachments) return

    const attachmentsData = parseAttachmentList(expense.attachments)
    if (attachmentsData.length > 0) {
      viewFileInModal(attachmentsData[0], expense.expense_type)
    }
  }

  const getFilteredExpenses = () => {
    return expenses.filter(expense => {
      const projectId = String(expense.project_id || '')
      const employeeId = String(expense.employee_id || '')
      const expenseType = String(expense.expense_type || '')
      const status = String(expense.status || '')
      const projectName = String(expense?.Project?.project_name || '')
      const expenseDate = toDateOnly(expense.expense_date)
      // Year / month derived from the expense date (YYYY-MM-DD string split — timezone-safe).
      const expenseYear = expenseDate ? expenseDate.split('-')[0] : ''
      const expenseMonth = expenseDate ? expenseDate.split('-')[1] : ''
      const matchProject = filters.project_ids.length === 0 || filters.project_ids.includes(projectId)
      const matchStatus = filters.statuses.length === 0 || filters.statuses.includes(status)
      const matchEmployee = filters.employee_ids.length === 0 || filters.employee_ids.includes(employeeId)
      const matchType = filters.expense_types.length === 0 || filters.expense_types.includes(expenseType)
      const matchProjectName = filters.project_names.length === 0 || filters.project_names.includes(projectName)
      const matchYear = filters.years.length === 0 || filters.years.includes(expenseYear)
      const matchMonth = filters.months.length === 0 || filters.months.includes(expenseMonth)

      return matchProject && matchStatus && matchEmployee && matchType && matchProjectName && matchYear && matchMonth
    })
  }

  const getMonthLabel = (monthValue) => {
    const monthNumber = Number(monthValue)
    if (!Number.isFinite(monthNumber) || monthNumber < 1 || monthNumber > 12) return '-'
    return new Intl.DateTimeFormat('en-US', { month: 'short' }).format(new Date(2000, monthNumber - 1, 1))
  }

  const getYearOptions = () => {
    return [...new Set(expenses.map((expense) => {
      const dateOnly = toDateOnly(expense.expense_date)
      return dateOnly ? dateOnly.split('-')[0] : ''
    }).filter(Boolean))].sort((a, b) => Number(b) - Number(a))
  }

  const getMonthOptions = () => {
    // Zero-padded month strings ("07") so option values match the derived
    // filter keys from the expense date string.
    return [...new Set(expenses.map((expense) => {
      const dateOnly = toDateOnly(expense.expense_date)
      return dateOnly ? dateOnly.split('-')[1] : ''
    }).filter((month) => {
      const monthNumber = Number(month)
      return Number.isFinite(monthNumber) && monthNumber >= 1 && monthNumber <= 12
    }))].sort((a, b) => Number(a) - Number(b))
  }

  // Calendar date of a DATE/datetime value, timezone-safe: reads the stored
  // date part directly instead of converting through UTC (toISOString shifts
  // dates back one day on UTC+ servers, putting day-1 dates in the wrong month).
  const toDateOnly = (value) => {
    if (!value) return ''
    const raw = String(value)
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (match) return `${match[1]}-${match[2]}-${match[3]}`
    const date = new Date(raw)
    if (Number.isNaN(date.getTime())) return ''
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
  }

  const getProjectOptions = () => {
    return projects.map((project) => ({
      value: String(project.project_id),
      label: `${project.po_no || project.project_code || 'N/A'} - ${project.project_name}`
    }))
  }

  const getEmployeeOptions = () => {
    return employees.map((employee) => ({
      value: String(employee.employee_id),
      label: employee.name
    }))
  }

  const getProjectNameOptions = () => {
    return [...new Set(expenses.map((expense) => String(expense?.Project?.project_name || '').trim()).filter(Boolean))].sort()
  }

  const filteredExpenses = getFilteredExpenses()
  const totalExpensePages = Math.max(1, Math.ceil(filteredExpenses.length / expenseRowsPerPage))
  const paginatedExpenses = filteredExpenses.slice((expensePage - 1) * expenseRowsPerPage, expensePage * expenseRowsPerPage)

  useEffect(() => {
    setExpensePage(1)
  }, [filters, expenseRowsPerPage])

  useEffect(() => {
    setExpensePage((prev) => Math.min(prev, totalExpensePages))
  }, [totalExpensePages])

  const resetFilters = () => {
    setFilters({
      years: [],
      months: [],
      project_ids: [],
      statuses: [],
      employee_ids: [],
      expense_types: [],
      project_names: []
    })
  }

  const getStatusBadge = (status) => {
    const statusColors = {
      'Pending': 'bg-yellow-100 text-yellow-800',
      'Approved': 'bg-green-100 text-green-800',
      'Rejected': 'bg-red-100 text-red-800'
    }
    return statusColors[status] || 'bg-gray-100 text-gray-800'
  }

  const getFileExtension = (filePath) => {
    return filePath?.split('.').pop()?.toLowerCase() || ''
  }

  const getAttachmentIcon = (ext) => {
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return '🖼️'
    return '📄'
  }

  const getAttachmentTooltip = (ext) => {
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image'
    return 'document'
  }

  const downloadFile = async (filePath, fileName) => {
    try {
      const fullUrl = filePath.startsWith('http') ? filePath : `${API_BASE_URL}${filePath}`
      const response = await fetch(fullUrl)
      if (!response.ok) throw new Error('Download failed')
      
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = fileName || fullUrl.split('/').pop() || 'file'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Download failed:', error)
      showMessage('Failed to download file', 'Error')
    }
  }

  const viewFileInModal = (filePath, expenseType = 'Receipt') => {
    const fullUrl = resolveAttachmentUrlByCategory(filePath, expenseType)
    if (!fullUrl) return
    setViewingFileInModal(fullUrl)
  }

  const openEditModal = (expense) => {
    setEditingExpense(expense)
    setEditFormData({
      amount: expense.amount || '',
      project_id: expense.project_id || '',
      po_id: expense.po_id || '',
      expense_category: expense.expense_category || '',
      description: expense.description || '',
      expense_date: expense.expense_date ? expense.expense_date.split('T')[0] : '',
      status: expense.status || 'Pending'
    })
    setEditFiles([])
    setZoomLevel(1)
    setShowEditModal(true)
  }

  const handleEditChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleSaveEdit = async () => {
    if (!editingExpense) return

    // Project is optional — an empty selection clears the project link (null)
    try {
      setLoading(true)
      if (editFiles.length > 0) {
        const formDataToSend = new FormData()
        formDataToSend.append('amount', editFormData.amount)
        formDataToSend.append('project_id', editFormData.project_id)
        formDataToSend.append('po_id', editFormData.po_id)
        formDataToSend.append('expense_category', editFormData.expense_category)
        formDataToSend.append('description', editFormData.description)
        formDataToSend.append('expense_date', editFormData.expense_date)
        formDataToSend.append('status', editFormData.status)
        formDataToSend.append('expense_type', editingExpense.expense_type || 'Receipt')
        editFiles.forEach((file) => formDataToSend.append('files', file))

        await expenseService.updateWithAttachments(
          editingExpense.expense_id,
          formDataToSend,
          editingExpense.expense_type || 'Receipt'
        )
      } else {
        await expenseService.update(editingExpense.expense_id, editFormData)
      }
      showMessage('Expense updated successfully!', 'Success')
      setShowEditModal(false)
      setEditFiles([])
      await fetchExpenses()
    } catch (error) {
      console.error('Failed to update expense:', error)
      showMessage(error.response?.data?.message || 'Failed to update expense', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteExpense = async (expenseId) => {
    const confirmed = await showConfirm('Are you sure you want to delete this expense?')
    if (!confirmed) {
      return
    }

    try {
      setLoading(true)
      await expenseService.delete(expenseId)
      showMessage('Expense deleted successfully!', 'Success')
      await fetchExpenses()
    } catch (error) {
      console.error('Failed to delete expense:', error)
      showMessage(error.response?.data?.message || 'Failed to delete expense', 'Error')
    } finally {
      setLoading(false)
    }
  }

  const FileIconButton = ({ onClick, icon, tooltip, disabled = false }) => {
    const [showTooltip, setShowTooltip] = useState(false)

    return (
      <div className="relative inline-block">
        <button
          onClick={(e) => {
            e.stopPropagation()
            onClick()
          }}
          disabled={disabled}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          className="text-blue-600 hover:text-blue-800 text-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
          title={tooltip}
        >
          {icon}
        </button>
        {showTooltip && !disabled && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap pointer-events-none">
            {tooltip}
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-800 sm:text-3xl">Expenses</h1>
        <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
          <button 
            onClick={() => {
              const formLink = `${window.location.origin}/expense-form-receipt`
              navigator.clipboard.writeText(formLink)
              showMessage('Receipt form link copied to clipboard!\n\n' + formLink, 'Success')
            }}
            className="w-full rounded-lg bg-green-600 px-4 py-2 font-medium text-white transition hover:bg-green-700 sm:w-auto"
            title="Share this link with employees to submit receipt expenses"
          >
            📄 Share Receipt Form
          </button>
          <button
            onClick={() => {
              const formLink = `${window.location.origin}/expense-form-advanced`
              navigator.clipboard.writeText(formLink)
              showMessage('Advanced form link copied to clipboard!\n\n' + formLink, 'Success')
            }}
            className="w-full rounded-lg bg-purple-600 px-4 py-2 font-medium text-white transition hover:bg-purple-700 sm:w-auto"
            title="Share this link with employees to submit advanced/prepaid expenses"
          >
            💰 Share Advanced Form
          </button>
          <button
            onClick={() => {
              const formLink = `${window.location.origin}/expense-form-project`
              navigator.clipboard.writeText(formLink)
              showMessage('Project Expenses form link copied to clipboard!\n\n' + formLink, 'Success')
            }}
            className="w-full rounded-lg bg-orange-600 px-4 py-2 font-medium text-white transition hover:bg-orange-700 sm:w-auto"
            title="Share this link with employees to submit project expenses (rental, material, others)"
          >
            🏗️ Share Project Expenses Form
          </button>
        </div>
      </div>
      {/* Filter Section */}
      <CollapsibleFilters>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Year</label>
            <MultiSelectFilterDropdown
              options={getYearOptions().map((yearValue) => String(yearValue))}
              selectedValues={filters.years}
              onChange={(years) => setFilters((prev) => ({ ...prev, years }))}
              placeholder="All years"
              emptyText="No years"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Month</label>
            <MultiSelectFilterDropdown
              options={getMonthOptions().map((monthValue) => ({ value: String(monthValue), label: getMonthLabel(monthValue) }))}
              selectedValues={filters.months}
              onChange={(months) => setFilters((prev) => ({ ...prev, months }))}
              placeholder="All months"
              emptyText="No months"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by PO No.</label>
            <MultiSelectFilterDropdown
              options={getProjectOptions()}
              selectedValues={filters.project_ids}
              onChange={(project_ids) => setFilters((prev) => ({ ...prev, project_ids }))}
              placeholder="All PO numbers"
              emptyText="No PO numbers"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Project Name</label>
            <MultiSelectFilterDropdown
              options={getProjectNameOptions()}
              selectedValues={filters.project_names}
              onChange={(project_names) => setFilters((prev) => ({ ...prev, project_names }))}
              placeholder="All project names"
              emptyText="No project names"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <MultiSelectFilterDropdown
              options={['Receipt', 'Advanced', 'Direct']}
              selectedValues={filters.expense_types}
              onChange={(expense_types) => setFilters((prev) => ({ ...prev, expense_types }))}
              placeholder="All types"
              emptyText="No types"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Status</label>
            <MultiSelectFilterDropdown
              options={['Pending', 'Approved', 'Rejected']}
              selectedValues={filters.statuses}
              onChange={(statuses) => setFilters((prev) => ({ ...prev, statuses }))}
              placeholder="All status"
              emptyText="No status options"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Employee</label>
            <MultiSelectFilterDropdown
              options={getEmployeeOptions()}
              selectedValues={filters.employee_ids}
              onChange={(employee_ids) => setFilters((prev) => ({ ...prev, employee_ids }))}
              placeholder="All employees"
              emptyText="No employees"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={resetFilters}
              className="btn-reset-standard"
            >
              🔄 Reset Filters
            </button>
          </div>
        </div>
      </CollapsibleFilters>

      <TableStateWrapper
        loading={loading}
        error={tableError}
        onRetry={fetchExpenses}
        loadingText="Loading expense records..."
        containerRef={expenseTableRef}
      >
        <table className="w-full">
          <thead className="bg-gray-100 border-b">
            <tr>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Expense No.</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">PO No.</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Project Name</th>
              <th className="px-3 py-2 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">Employee</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Type</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Amount</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Category</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Remarks</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Submitted</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Expense Date</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Status</th>
              <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Attachment</th>
              {!isViewer && <th className="px-3 py-2 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">Action</th>}
            </tr>
          </thead>
          <tbody>
            {filteredExpenses.length === 0 ? (
              <tr>
                <td colSpan={isViewer ? '12' : '13'} className="px-3 py-2 text-center text-gray-500">
                  No expenses found. Submit your first expense!
                </td>
              </tr>
            ) : (
              paginatedExpenses.map((expense) => (
                <tr 
                  key={expense.expense_id} 
                  className="border-b hover:bg-gray-100 cursor-pointer transition"
                  onClick={() => openEditModal(expense)}
                >
                  <td className="px-3 py-2 text-left text-sm font-medium text-blue-600 whitespace-nowrap">
                    {expense.expense_code || `EXP-${String(expense.expense_id).padStart(5, '0')}`}
                  </td>
                  <td className="px-3 py-2 text-left text-sm text-gray-600 whitespace-nowrap">
                    {expense.Project?.po_no || expense.Project?.project_code || '-'}
                  </td>
                  <td className="px-3 py-2 text-left text-sm text-gray-600 whitespace-nowrap">
                    <TruncatedText text={expense.Project?.project_name} />
                  </td>
                  <td className="px-3 py-2 text-left text-sm text-gray-800 whitespace-nowrap">
                    <TruncatedText text={expense.Employee?.name} />
                  </td>
                  <td className="px-3 py-2 text-sm text-center whitespace-nowrap">
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                      {expense.expense_type}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm text-center font-semibold text-gray-800 whitespace-nowrap">
                    {expense.amount ? formatCurrency(expense.amount) : '-'}
                  </td>
                  <td className="px-3 py-2 text-sm text-center whitespace-nowrap">
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                      {expense.expense_category ? expense.expense_category : 'N/A'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-sm text-center text-gray-600 whitespace-nowrap">
                    <TruncatedText text={expense.description} />
                  </td>
                  <td className="px-3 py-2 text-sm text-center text-gray-600 whitespace-nowrap">
                    {formatDate(expense.submission_date)}
                  </td>
                  <td className="px-3 py-2 text-sm text-center text-gray-600 whitespace-nowrap">
                    {expense.expense_date ? formatDate(expense.expense_date) : '-'}
                  </td>
                  <td className="px-3 py-2 text-sm text-center whitespace-nowrap">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(expense.status)}`}>
                      {expense.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center whitespace-nowrap">
                    {expense.attachments && (() => {
                      const firstFile = parseAttachmentList(expense.attachments)[0]
                      const fullUrl = firstFile ? resolveAttachmentUrlByCategory(firstFile, expense.expense_type) : ''
                      const ext = fullUrl ? getFileExtension(fullUrl) : ''
                      return fullUrl ? (
                        <FileIconButton
                          onClick={() => openFirstFile(expense)}
                          icon={getAttachmentIcon(ext)}
                          tooltip={`View ${getAttachmentTooltip(ext)}`}
                        />
                      ) : null
                    })()}
                  </td>
                  {!isViewer && (
                    <td className="px-3 py-2 text-center whitespace-nowrap">
                        <button
                          onClick={() => openEditModal(expense)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium mr-3"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteExpense(expense.expense_id)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
          {filteredExpenses.length > 0 && (
            <tfoot className="sticky bottom-0 bg-gray-100 shadow-[0_-2px_6px_rgba(0,0,0,0.08)]">
              <tr>
                <td colSpan={6} className="px-3 py-2.5 text-right text-sm font-semibold text-gray-700 whitespace-nowrap">
                  Total ({filteredExpenses.length} expense{filteredExpenses.length !== 1 ? 's' : ''}) — Total Amount:
                </td>
                <td className="px-3 py-2.5 text-center text-sm font-bold text-gray-900 whitespace-nowrap">
                  {formatCurrency(filteredExpenses.reduce((sum, expense) => sum + (Number(expense.amount) || 0), 0))}
                </td>
                <td colSpan={isViewer ? 5 : 6} />
              </tr>
            </tfoot>
          )}
        </table>
        <TablePagination
          currentPage={expensePage}
          totalPages={totalExpensePages}
          totalItems={filteredExpenses.length}
          rowsPerPage={expenseRowsPerPage}
          onPrevious={() => setExpensePage((prev) => Math.max(prev - 1, 1))}
          onNext={() => setExpensePage((prev) => Math.min(prev + 1, totalExpensePages))}
        />
      </TableStateWrapper>

      {/* File Viewer Modal */}
      {viewingFileInModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setViewingFileInModal(null)}>
          <div className="bg-white rounded-lg w-full h-full max-w-4xl max-h-screen overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b bg-gray-50">
              <h2 className="text-lg font-bold text-gray-800">File Viewer</h2>
              <button
                onClick={() => setViewingFileInModal(null)}
                className="modal-close-button"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-auto flex flex-col items-center justify-center bg-gray-100">
              {['pdf'].includes(getFileExtension(viewingFileInModal)) ? (
                <iframe
                  src={viewingFileInModal}
                  className="w-full h-full border-none"
                  title="Document Viewer"
                />
              ) : ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(getFileExtension(viewingFileInModal)) ? (
                <div className="flex flex-col items-center justify-center w-full h-full">
                  <img 
                    src={viewingFileInModal} 
                    alt="Attachment" 
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'flex'
                    }}
                  />
                  <div className="hidden flex-col items-center justify-center w-full h-full text-gray-600">
                    <p className="text-lg mb-4">Unable to preview image</p>
                    <button
                      onClick={() => downloadFile(viewingFileInModal, 'image')}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium"
                    >
                      Download Image
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center text-gray-600">
                  <div className="text-6xl mb-4">📄</div>
                  <p className="text-lg mb-2">File type not directly viewable</p>
                  <p className="text-sm text-gray-500 mb-6">{getFileExtension(viewingFileInModal).toUpperCase()} file</p>
                  <button
                    onClick={() => downloadFile(viewingFileInModal, 'file')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium"
                  >
                    Download File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Expense Modal */}
      {showEditModal && editingExpense && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4" onClick={() => { setZoomLevel(1); setShowEditModal(false); }}>
          <div className="modal-panel-standard bg-white max-w-6xl w-full sm:rounded-lg h-[92vh] sm:h-[85vh] lg:h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center gap-3 p-3 sm:p-4 border-b bg-gray-50">
              <h2 className="text-base sm:text-lg font-bold text-gray-800 truncate min-w-0">
                Edit Expense No. {editingExpense.expense_code}
              </h2>
              <button
                onClick={() => { setZoomLevel(1); setShowEditModal(false); }}
                className="modal-close-button flex-shrink-0"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">
              {/* File Viewer on Left */}
              <div className="w-full lg:w-[58%] min-h-[35vh] lg:min-h-0 bg-gray-100 flex flex-col border-b lg:border-b-0 lg:border-r">
                {/* Zoom Controls */}
                <div className="flex flex-wrap gap-2 p-2 sm:p-3 bg-gray-200 border-b items-center justify-center">
                  <button
                    onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.2))}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm font-medium"
                    title="Zoom Out"
                  >
                    −
                  </button>
                  <span className="text-sm font-medium text-gray-700 min-w-[60px] text-center">{Math.round(zoomLevel * 100)}%</span>
                  <button
                    onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.2))}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm font-medium"
                    title="Zoom In"
                  >
                    +
                  </button>
                  <button
                    onClick={() => setZoomLevel(1)}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm font-medium ml-2"
                    title="Reset Zoom"
                  >
                    Reset
                  </button>
                </div>

                {/* File Viewer */}
                <div className="flex-1 overflow-auto p-2">
                  {editingExpense.attachments ? (
                    (() => {
                      const attachmentsData = parseAttachmentList(editingExpense.attachments)
                      const firstFile = attachmentsData[0]

                      if (!firstFile) {
                        return <div className="text-center text-gray-500"><p>No file attached</p></div>
                      }

                      const fullUrl = resolveAttachmentUrlByCategory(firstFile, editingExpense.expense_type)
                      if (!fullUrl) {
                        return <div className="text-center text-gray-500"><p>No file attached</p></div>
                      }
                      const ext = getFileExtension(fullUrl)

                      return (
                        <div className="w-full h-full flex items-center justify-center">
                          {['pdf'].includes(ext) ? (
                            <iframe
                              src={fullUrl}
                              className="w-full h-full border-none rounded bg-white"
                              title="Document Viewer"
                              style={{
                                transform: `scale(${zoomLevel})`,
                                transformOrigin: 'center center'
                              }}
                            />
                          ) : ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext) ? (
                            <img 
                              src={fullUrl} 
                              alt="Attachment" 
                              className="max-w-full max-h-full object-contain rounded"
                              style={{
                                transform: `scale(${zoomLevel})`,
                                transformOrigin: 'center center'
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none'
                              }}
                            />
                          ) : (
                            <div className="text-center text-gray-600">
                              <div className="text-6xl mb-4">📄</div>
                              <p className="text-sm text-gray-500">{ext.toUpperCase()} file</p>
                              <button
                                onClick={() => downloadFile(fullUrl, 'file')}
                                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm"
                              >
                                Download
                              </button>
                            </div>
                          )}
                        </div>
                      )
                    })()
                  ) : (
                    <div className="text-center text-gray-500">No attachments</div>
                  )}
                </div>
              </div>

              {/* Edit Form on Right */}
              <div className="w-full lg:flex-1 p-4 sm:p-6 overflow-y-auto">
                <form className="space-y-4">
                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Amount (RM) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={editFormData.amount}
                      onChange={(e) => handleEditChange('amount', e.target.value)}
                      placeholder="0.00"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    {editingExpense.expense_type === 'Advanced' ? (
                      <div className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600">
                        N/A (Advanced expenses have no category)
                      </div>
                    ) : (
                      <select
                        value={editFormData.expense_category}
                        onChange={(e) => handleEditChange('expense_category', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">-- Select Category --</option>
                        <optgroup label="General & Administrative Expenses">
                          <option value="Account Fees">Account Fees</option>
                          <option value="Insurance & Roadtax">Insurance & Roadtax</option>
                          <option value="Office Supplies">Office Supplies</option>
                          <option value="Professional Fees">Professional Fees</option>
                          <option value="Admin Fees">Admin Fees</option>
                        </optgroup>
                        <optgroup label="Operating Expenses">
                          <option value="Project Management Fees">Project Management Fees</option>
                          <option value="Travelling & Accommodation">Travelling & Accommodation</option>
                          <option value="Repair & Maintenance">Repair & Maintenance</option>
                          <option value="Rental">Rental</option>
                          <option value="Site Expenses">Site Expenses</option>
                          <option value="Project Commission">Project Commission</option>
                        </optgroup>
                        <optgroup label="Employment Expenses">
                          <option value="Staff Welfare">Staff Welfare</option>
                          <option value="Wages & Salaries">Wages & Salaries</option>
                          <option value="Staff Bonus">Staff Bonus</option>
                        </optgroup>
                        <optgroup label="Cost of Sales">
                          <option value="Materials & Supplies">Materials & Supplies</option>
                          <option value="Subcontractor Costs">Subcontractor Costs</option>
                          <option value="Transportation & Rental Services">Transportation & Rental Services</option>
                          <option value="Site Insurance">Site Insurance</option>
                        </optgroup>
                        <optgroup label="Other">
                          <option value="Petrol">Petrol</option>
                          <option value="Others">Others</option>
                        </optgroup>
                      </select>
                    )}
                  </div>

                  {/* Expense Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expense Date
                    </label>
                    <input
                      type="date"
                      value={editFormData.expense_date}
                      onChange={(e) => handleEditChange('expense_date', e.target.value)}
                      className="modal-date-input w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Based on receipt date</p>
                  </div>

                  {/* Project */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      PO No.
                    </label>
                    <select
                      value={editFormData.po_id}
                      onChange={(e) => handleEditChange('po_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">-- Select PO No. --</option>
                      {purchaseOrders.map((po) => (
                        <option key={po.po_id} value={po.po_id}>
                          {po.po_number || po.project_code || po.project_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Remarks
                    </label>
                    <input
                      type="text"
                      value={editFormData.description}
                      onChange={(e) => handleEditChange('description', e.target.value)}
                      placeholder="Enter description..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={editFormData.status}
                      onChange={(e) => handleEditChange('status', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="Pending">Pending</option>
                      <option value="Approved">Approved</option>
                      <option value="Rejected">Rejected</option>
                    </select>
                  </div>

                  {/* Upload Image — single file, replaces existing */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Image (replaces existing)
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.zip"
                      onChange={(e) => {
                        const picked = e.target.files?.[0] || null
                        setEditFiles(picked ? [picked] : [])
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {editFiles.length > 0 && (
                      <p className="text-xs text-gray-600 mt-1">
                        1 image selected — will replace the current attachment.
                      </p>
                    )}
                  </div>

                  {/* Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-gray-600 break-words">
                      <span className="font-semibold">📝 Employee:</span> {editingExpense.Employee?.name}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 break-words">
                      <span className="font-semibold">📅 Submitted:</span> {formatDate(editingExpense.submission_date)}
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-4 pb-2">
                    <button
                      type="button"
                      onClick={handleSaveEdit}
                      className="flex-1 px-4 py-2.5 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
                      disabled={loading || !canMutate}
                    >
                      {loading ? 'Updating...' : 'Update'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setZoomLevel(1); setShowEditModal(false); }}
                      className="flex-1 px-4 py-2.5 sm:py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
      {modalElement}
    </div>
  )
}
